import { Box, chakra, GridItem } from '@chakra-ui/react'
import { createContext, useState } from 'react'

import ComponentProps from '../../types/ComponentProps'
import { Main } from './Main'
import { InsertProps, NavigationContextType, LinkRowLinkProps } from './types'
import LinkRow from './LinkRow'
import Sub, { SubWrapper } from './Sub'

export const NavigationContext = createContext<NavigationContextType>({
  isOpen: false,
  setIsOpen: () => {},
  activeIndex: undefined,
  setActiveIndex: () => {},
})

/**
 * A Navigation bar component with customiseable sub elements using [Compound Components](https://www.patterns.dev/react/compound-pattern/)
 *
 * This mainly fuctions as a wrapper component that keeps track of the state and context.
 *
 * A subset of components are available via the compound pattern.
 * The main ones are {@link Main | `<Navigation.Main/>`} and {@link Sub | `<Navigation.Sub/>`}, which are used to structure the main and sub parts of the navigation.
 *
 * All available compounds are:
 *
 * {@link Main | `<Navigation.Main/>`}
 *
 * {@link LinkRow | `<Navigation.LinkRow/>`}
 *
 * {@link Insert | `<Navigation.Insert/>`}
 *
 * {@link Sub | `<Navigation.Sub/>`}
 *
 * {@link SubWrapper | `<Navigation.SubWrapper/>`}
 *
 *
 * @param className - An optional classname to pass styling through a css file.
 * @param theme - The theme to be used from your theme config.
 * @param children - Child elements to render.
 *
 * @returns {JSX.Element}
 */
const Navigation = ({
  className,
  theme = 'brand',
  children,
  ...props
}: ComponentProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined)

  return (
    <NavigationContext.Provider
      value={{ isOpen, setIsOpen, activeIndex, setActiveIndex }}
    >
      <Box
        className={className}
        onMouseLeave={() => setIsOpen(false)}
        {...props}
      >
        {children}
      </Box>
    </NavigationContext.Provider>
  )
}

const Insert = ({ colStart, colSpan = 2, children, ...props }: InsertProps) => {
  return (
    <GridItem colSpan={colSpan} colStart={colStart} {...props}>
      {children}
    </GridItem>
  )
}

/**
 * A wrapper for the content in the 'main' navigation bar. This is a [Compound Component](https://www.patterns.dev/react/compound-pattern/) for {@link Navigation | `<Navigation/>`}
 *
 * Renders as a [ChakraUI `<Grid />`](https://v2.chakra-ui.com/docs/components/grid/usage)
 *
 * @param className - An optional classname to pass styling through a css file.
 * @param theme - The theme to be used from your theme config.
 * @param children - Child elements to render.
 *
 * @returns {JSX.Element}
 */
Navigation.Main = chakra(Main)

/**
 * A Row with link elements. This is a [Compound Component](https://www.patterns.dev/react/compound-pattern/) for {@link Navigation | `<Navigation/>`}
 *
 * Renders as a [ChakraUI `<GridItem />`](https://v2.chakra-ui.com/docs/components/grid/usage)
 *
 * @param colStart - Which column this element should start at.
 * @param colSpan- The amount of columns this element should span.
 * @param items - Data for link elements to render. Array of items with text, url and subItems: {@link LinkRowLinkProps | `LinkRowLinkProps`}
 * @param hasSubMenu - Whether this link row can trigger the opening of a sub menu.
 * @param title - Title of this section of links. Works best when rendered as a column layout.
 * @param className - An optional classname to pass styling through a css file.
 * @param theme - The theme to be used from your theme config.
 * @param children - Child elements to render.
 *
 * @returns {JSX.Element}
 */
Navigation.LinkRow = chakra(LinkRow)

/**
 * A navigation insert. This is a [Compound Component](https://www.patterns.dev/react/compound-pattern/) for {@link Navigation | `<Navigation/>`}
 *
 * Use this for inserting custom components in the main or sub navigation, or just for spacing.
 *
 * Renders as a [ChakraUI `<GridItem />`](https://v2.chakra-ui.com/docs/components/grid/usage)
 *
 * @param colStart - Which column this element should start at.
 * @param colSpan- The amount of columns this element should span.
 * @param children - Child elements to render.
 *
 * @returns {JSX.Element}
 */
Navigation.Insert = chakra(Insert)

/**
 * A wrapper for different sub menus. This is a [Compound Component](https://www.patterns.dev/react/compound-pattern/) for {@link Navigation | `<Navigation/>`}
 *
 * @param subKey - The key that matches the index of the item in the main navigation, so the correct content shows when triggered.
 * @param children - Child elements to render.
 *
 * @returns {JSX.Element}
 */
Navigation.SubWrapper = chakra(SubWrapper)

/**
 * A collapsable sub navigation. This is a [Compound Component](https://www.patterns.dev/react/compound-pattern/) for {@link Navigation | `<Navigation/>`}
 *
 * Makes use of the [ChakraUI `<SlideFade />`](https://v2.chakra-ui.com/docs/components/transitions/usage)
 *
 * @param YOffset - Optional offset for the slide element, useful when creating a 'rounded' navigation where the sub needs to overlap behind the main bar.
 * @param className - An optional classname to pass styling through a css file.
 * @param theme - The theme to be used from your theme config.
 * @param children - Child elements to render.
 *
 * @returns {JSX.Element}
 */
Navigation.Sub = chakra(Sub)

export default Navigation
